import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiUrl, chainUrl } from '../../config';
import { canonicalizeRoseData } from '../../api/client';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ExternalLink,
  FileArchive,
  FileImage,
  ImageIcon,
  Loader2,
  LockKeyhole,
  PackagePlus,
  RefreshCw,
  Rocket,
  Settings,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  Wallet,
  X,
} from 'lucide-react';
import { useAppKit, useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import {
  PublicKey,
  SystemProgram,
  TransactionInstruction,
} from '@solana/web3.js';
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import {
  generateSigner,
  none,
  publicKey,
  some,
  transactionBuilder,
  unwrapOption,
} from '@metaplex-foundation/umi';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';
import { fromWeb3JsInstruction } from '@metaplex-foundation/umi-web3js-adapters';
import {
  mplCore,
  updateCollectionV1,
} from '@metaplex-foundation/mpl-core';

import {
  fetchCandyGuard,
  fetchCandyMachine,
  mintV1,
  mplCandyMachine,
} from '@metaplex-foundation/mpl-core-candy-machine';
import JSZip from 'jszip';
import bs58 from 'bs58';


const configured = true;
const RELEASE = 'ROSE_CREATOR_LAUNCH_WIZARD_R46_UPLOAD_OR_BASE_URI';
const ORCHESTRATOR = '/chain-builder/orchestrator';

// Browser wallet transactions must always use the frontend's configured
// /chain-builder proxy.  On localhost this keeps JSON-RPC same-origin and lets
// Vite proxy it to production; on the live app it stays on the live Rose host.
// Do not trust a canonical browserRpcUrl returned by the backend here because
// a localhost request is intentionally proxied and the backend cannot infer the
// browser-visible origin reliably through every reverse proxy.
function browserRpcProxyUrl() {
  const value = chainUrl(`${ORCHESTRATOR}/rpc`);
  if (/^https?:\/\//i.test(value)) return value;
  if (typeof window !== 'undefined') {
    return new URL(value, window.location.origin).toString();
  }
  return value;
}
const CORE_PROGRAM = 'CoREENxT6tW1HoK8ypY1SxRMZTcVPm7R94rH4PZNhX7d';
const MEMO_PROGRAM = 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr';
const ACCEPTED_MEDIA = /\.(png|jpe?g|webp|gif|mp4|webm|mov|glb)$/i;

function apiErrorMessage(payload, status) {
  const detail = payload?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const first = (detail.errors || detail.setup?.errors || detail.validation?.errors || [])[0];
    return detail.message || first || JSON.stringify(detail);
  }
  return payload?.error || payload?.message || `Request failed (${status})`;
}

async function jsonRequest(path, options = {}) {
  const response = await fetch(path, {
    credentials: 'include',
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(apiErrorMessage(payload, response.status));
  return canonicalizeRoseData(payload);
}

const api = (path, options = {}) => jsonRequest(apiUrl(`/api/v1${path}`), options);
const orch = (path, options = {}) => jsonRequest(chainUrl(`${ORCHESTRATOR}${path}`), options);
const short = (value = '') => value ? `${value.slice(0, 7)}…${value.slice(-6)}` : '—';
const pendingMintKey = launchId =>
  `onlyrose:pending-mint:${launchId}`;

const LAUNCH_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

async function resolvePublicLaunchId(value) {
  const candidate = String(value || '').trim();
  if (LAUNCH_UUID_RE.test(candidate)) return candidate;
  if (!candidate) throw new Error('Launch identifier is missing');

  const response = await api('/launchpad?limit=100');
  const rows = Array.isArray(response)
    ? response
    : (response?.launches || response?.items || []);
  const match = rows.find(row =>
    [
      row?.collection_address,
      row?.candy_machine_address,
      row?.launch_id,
      row?.launchId,
    ].some(item => String(item || '') === candidate),
  );
  const resolved = String(match?.id || match?.launch_id || match?.launchId || '');

  if (!LAUNCH_UUID_RE.test(resolved)) {
    throw new Error('This collection does not have a valid public launch record');
  }
  return resolved;
}

function PublicMintResolver({ launchId, open, account, walletProvider }) {
  const direct = LAUNCH_UUID_RE.test(String(launchId || '')) ? String(launchId) : '';
  const [resolvedLaunchId, setResolvedLaunchId] = useState(direct);
  const [resolveMessage, setResolveMessage] = useState(direct ? '' : 'Resolving live mint…');

  useEffect(() => {
    let live = true;
    const nextDirect = LAUNCH_UUID_RE.test(String(launchId || '')) ? String(launchId) : '';
    if (nextDirect) {
      setResolvedLaunchId(nextDirect);
      setResolveMessage('');
      return () => { live = false; };
    }

    setResolvedLaunchId('');
    setResolveMessage('Resolving live mint…');
    resolvePublicLaunchId(launchId)
      .then(id => {
        if (!live) return;
        setResolvedLaunchId(id);
        setResolveMessage('');
      })
      .catch(error => {
        if (!live) return;
        setResolveMessage(error?.message || 'Live mint not found');
      });

    return () => { live = false; };
  }, [launchId]);

  if (resolvedLaunchId) {
    return <PublicMint launchId={resolvedLaunchId} open={open} account={account} walletProvider={walletProvider} />;
  }

  return (
    <main className="app-shell public-mint">
      <Header account={account} open={open} message={resolveMessage} />
      <section className="mint-panel">
        <span>ONLYROSE PUBLIC MINT</span>
        <h2>Collection mint</h2>
        <p>{resolveMessage}</p>
        <div className="mint-stats">
          <div><b>—</b><small>Minted</small></div>
          <div><b>—</b><small>Creator price</small></div>
          <div><b>—</b><small>Rose fee</small></div>
          <div><b>—</b><small>Total</small></div>
        </div>
        <button className="launch-button" disabled>
          <Loader2 className="spin" />
          Loading mint
        </button>
      </section>
    </main>
  );
}

async function uploadPublicMedia(
  collectionId,
  file,
  metadata = {},
) {
  const response = await fetch(
    chainUrl(`${ORCHESTRATOR}/collections/${collectionId}/media`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type':
          file.type ||
          'application/octet-stream',
        'X-OnlyRose-File-Name':
          encodeURIComponent(
            file.name,
          ),
        'X-OnlyRose-Metadata':
          encodeURIComponent(
            JSON.stringify(
              metadata || {},
            ),
          ),
      },
      body: file,
    },
  );
  const payload =
    await response
      .json()
      .catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      apiErrorMessage(
        payload,
        response.status,
      ),
    );
  }

  return canonicalizeRoseData(payload);
}

const sol = lamports => `${(Number(lamports || 0) / 1e9).toLocaleString(undefined, { maximumFractionDigits: 9 })} SOL`;
const bytesToBase64 = bytes => {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
};
const signatureText = value => {
  const raw = value?.signature || value;
  return typeof raw === 'string' ? raw : bs58.encode(Uint8Array.from(raw));
};

function walletAdapter(address, provider) {
  return {
    name: 'Reown Solana',
    url: window.location.origin,
    icon: '',
    readyState: 'Installed',
    connected: true,
    publicKey: new PublicKey(address),
    signTransaction: transaction => provider.signTransaction(transaction),
    signAllTransactions: transactions =>
      provider.signAllTransactions
        ? provider.signAllTransactions(transactions)
        : Promise.all(transactions.map(transaction => provider.signTransaction(transaction))),
    signMessage: message => provider.signMessage(message),
  };
}

async function ensureVerified(address, walletProvider) {
  if (!address || !walletProvider?.signMessage) {
    throw new Error('Connect a Solana wallet that supports message signing');
  }
  const session = await api('/auth/session').catch(() => null);
  if (
    session?.wallets?.some?.(
      wallet => wallet.namespace === 'solana' && wallet.address === address,
    )
  ) {
    return session;
  }
  const challenge = await api('/auth/challenge', {
    method: 'POST',
    body: JSON.stringify({ namespace: 'solana', address }),
  });
  const signed = await walletProvider.signMessage(
    new TextEncoder().encode(challenge.message),
  );
  const raw = signed?.signature || signed;
  await api('/auth/verify/solana', {
    method: 'POST',
    body: JSON.stringify({
      challenge_id: challenge.challenge_id,
      address,
      signature: typeof raw === 'string' ? raw : bytesToBase64(raw),
    }),
  });
  return api('/auth/session');
}

function approvalInstruction(instruction) {
  return {
    instruction: fromWeb3JsInstruction(instruction),
    signers: [],
    bytesCreatedOnChain: 0,
  };
}

async function sendOneLaunchApproval({
  account,
  walletProvider,
  job,
  browserRpcUrl,
}) {
  const umi = createUmi(browserRpcUrl)
    .use(mplCore())
    .use(
      walletAdapterIdentity(
        walletAdapter(account.address, walletProvider),
      ),
    );

  let builder = transactionBuilder()
    .add(
      approvalInstruction(
        SystemProgram.transfer({
          fromPubkey: new PublicKey(account.address),
          toPubkey: new PublicKey(job.roseTreasury),
          lamports: Number(job.launchFeeLamports),
        }),
      ),
    )
    .add(
      approvalInstruction(
        SystemProgram.transfer({
          fromPubkey: new PublicKey(account.address),
          toPubkey: new PublicKey(job.jobSigner),
          lamports: Number(job.reserveLamports),
        }),
      ),
    )
    .add(
      approvalInstruction(
        new TransactionInstruction({
          programId: new PublicKey(MEMO_PROGRAM),
          keys: [],
          data: new TextEncoder().encode(`onlyrose-launch:${job.id}`),
        }),
      ),
    );

  if (job.collectionAddress) {
    builder = builder.add(
      updateCollectionV1(umi, {
        collection: publicKey(job.collectionAddress),
        authority: umi.identity,
        newUpdateAuthority: publicKey(job.jobSigner),
        newName: none(),
        newUri: none(),
      }),
    );
  }

  const result = await builder.send(umi);
  return signatureText(result);
}


async function sendFundingTopUp({
  account,
  walletProvider,
  job,
  browserRpcUrl,
  shortfallLamports = null,
  memoPrefix = 'onlyrose-topup',
}) {
  const shortfall = Number(
    shortfallLamports ??
    job?.funding?.shortfallLamports ??
    0,
  );

  if (
    !Number.isSafeInteger(shortfall) ||
    shortfall <= 0
  ) {
    throw new Error(
      'No refundable funding shortfall remains',
    );
  }

  const umi = createUmi(browserRpcUrl)
    .use(mplCore())
    .use(
      walletAdapterIdentity(
        walletAdapter(
          account.address,
          walletProvider,
        ),
      ),
    );

  const result = await transactionBuilder()
    .add(
      approvalInstruction(
        SystemProgram.transfer({
          fromPubkey:
            new PublicKey(
              account.address,
            ),
          toPubkey:
            new PublicKey(
              job.jobSigner,
            ),
          lamports: shortfall,
        }),
      ),
    )
    .add(
      approvalInstruction(
        new TransactionInstruction({
          programId:
            new PublicKey(
              MEMO_PROGRAM,
            ),
          keys: [],
          data:
            new TextEncoder().encode(
              `${memoPrefix}:${job.id}`,
            ),
        }),
      ),
    )
    .send(umi);

  return signatureText(result);
}

function Stepper({ step }) {
  const items = [
    ['Collection', 1],
    ['NFT files', 2],
    ['Mint', 3],
    ['Review', 4],
  ];
  return (
    <div className="stepper" aria-label="Launch progress">
      {items.map(([label, number]) => (
        <div
          key={label}
          className={number === step ? 'active' : number < step ? 'complete' : ''}
        >
          <i>{number < step ? <Check size={15} /> : number}</i>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function Header({ account, open, message }) {
  return (
    <>
      <header className="topbar">
        <a className="brand-link" href="/">
          <span className="rose-mark">R</span>
          <span>
            <b>ONLYROSE</b>
            <small>Creator launch</small>
          </span>
        </a>
        <div className="top-title">
          <span>SOLANA MAINNET</span>
          <h1>Launch an NFT collection</h1>
          <p>Upload, review once, approve once. Rose handles the deployment.</p>
        </div>
        <button
          className="wallet-button"
          onClick={() => configured ? open({ view: 'Connect' }) : null}
        >
          <Wallet size={18} />
          {account.address ? short(account.address) : 'Connect wallet'}
        </button>
      </header>
      {message && <div className="message-bar">{message}</div>}
    </>
  );
}

function Dropzone({ onFiles, busy, files }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const choose = event => onFiles([...(event.target.files || [])]);
  return (
    <div
      className={`dropzone ${dragging ? 'dragging' : ''}`}
      onDragEnter={event => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={event => event.preventDefault()}
      onDragLeave={() => setDragging(false)}
      onDrop={event => {
        event.preventDefault();
        setDragging(false);
        onFiles([...event.dataTransfer.files]);
      }}
      onClick={() => !busy && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        hidden
        multiple
        type="file"
        accept=".zip,.json,image/*,video/*,.glb"
        onChange={choose}
      />
      {busy ? <Loader2 className="spin" size={38} /> : <UploadCloud size={38} />}
      <b>{busy ? 'Preparing NFT files…' : 'Drop NFT files or one ZIP here'}</b>
      <span>Media can include matching JSON metadata, or Rose creates it automatically.</span>
      {!!files.length && <small>{files.length} file{files.length === 1 ? '' : 's'} ready</small>}
    </div>
  );
}

function CollectionStep({
  collections,
  collection,
  form,
  setForm,
  selectCollection,
  save,
  busy,
}) {
  return (
    <section className="wizard-card">
      <div className="card-heading">
        <i>1</i>
        <div>
          <h2>Collection</h2>
          <p>Name it and keep the rest minimal.</p>
        </div>
      </div>
      <label className="field full">
        <span>Use an existing collection or create a new one</span>
        <select
          value={collection?.id || ''}
          onChange={event => selectCollection(event.target.value)}
        >
          <option value="">New collection</option>
          {collections.map(item => (
            <option key={item.id} value={item.id}>{item.name}</option>
          ))}
        </select>
      </label>
      <div className="form-grid">
        <label className="field">
          <span>Collection name</span>
          <input
            value={form.name}
            onChange={event => setForm({ ...form, name: event.target.value })}
            placeholder="Red Rose"
          />
        </label>
        <label className="field">
          <span>Symbol</span>
          <input
            maxLength={10}
            value={form.symbol}
            onChange={event => setForm({ ...form, symbol: event.target.value.toUpperCase() })}
            placeholder="ROSE"
          />
        </label>
        <label className="field full">
          <span>Description</span>
          <textarea
            value={form.description}
            onChange={event => setForm({ ...form, description: event.target.value })}
            placeholder="Tell collectors what this collection is."
          />
        </label>
      </div>
      <div className="actions end">
        <button
          className="primary"
          disabled={busy || !form.name.trim()}
          onClick={save}
        >
          {busy ? <Loader2 className="spin" /> : <ArrowRight />}
          {collection ? 'Save and continue' : 'Create and continue'}
        </button>
      </div>
    </section>
  );
}

function MediaStep({
  collection,
  parsed,
  onFiles,
  upload,
  retainMedia,
  clearMedia,
  cancelLaunch,
  busy,
  uploadProgress,
  back,
  metadataSourceMode,
  setMetadataSourceMode,
  externalBaseUri,
  setExternalBaseUri,
  externalItemCount,
  setExternalItemCount,
  externalProbe,
  externalBusy,
  probeExternalMetadata,
  useExternalMetadata,
}) {
  const existing = (collection?.assets || [])
    .filter(item => item.role === 'item')
    .slice()
    .sort((left, right) => Number(left.position || 0) - Number(right.position || 0));
  const metadataCount = collection?.metadata_items?.length || 0;
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const allowed = new Set(existing.map(item => String(item.id)));
    setSelected(current => current.filter(id => allowed.has(String(id))));
  }, [collection?.id, existing.map(item => item.id).join('|')]);

  const toggle = id => {
    setSelected(current =>
      current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id],
    );
  };

  const removeSelected = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Remove ${selected.length} selected NFT file${selected.length === 1 ? '' : 's'} from this unlaunched draft?`)) return;
    const selectedSet = new Set(selected.map(String));
    const retain = existing
      .map(item => String(item.id))
      .filter(id => !selectedSet.has(id));
    const ok = await retainMedia(retain);
    if (ok !== false) setSelected([]);
  };

  const clearAll = async () => {
    if (!existing.length) return;
    if (!window.confirm(`Clear all ${existing.length} NFT files from this unlaunched draft?`)) return;
    const ok = await clearMedia();
    if (ok !== false) setSelected([]);
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this unlaunched launch setup and clear its attached NFT files? The collection record is kept so you can start again.')) return;
    const ok = await cancelLaunch();
    if (ok !== false) setSelected([]);
  };

  const probeReady =
    externalProbe &&
    !externalProbe.error &&
    (externalProbe.first_index === 0 || externalProbe.first_index === 1);
  const validIndexes = Array.isArray(externalProbe?.valid_indexes)
    ? externalProbe.valid_indexes
    : [];

  return (
    <section className="wizard-card">
      <div className="card-heading">
        <i>2</i>
        <div>
          <h2>NFT source</h2>
          <p>Choose simple file upload or an existing metadata Base URI.</p>
        </div>
      </div>

      <div className="metadata-source-switch" role="group" aria-label="NFT source">
        <button
          type="button"
          className={`metadata-source-choice${metadataSourceMode === 'upload' ? ' active' : ''}`}
          onClick={() => setMetadataSourceMode('upload')}
          disabled={busy || externalBusy}
        >
          <UploadCloud />
          <span>
            <b>Upload / drag & drop</b>
            <small>Images, video or ZIP. Matching JSON is optional.</small>
          </span>
        </button>
        <button
          type="button"
          className={`metadata-source-choice${metadataSourceMode === 'external' ? ' active' : ''}`}
          onClick={() => setMetadataSourceMode('external')}
          disabled={busy || externalBusy}
        >
          <ExternalLink />
          <span>
            <b>Use existing metadata Base URI</b>
            <small>Rose detects 0.json or 1.json and imports the collection.</small>
          </span>
        </button>
      </div>

      {metadataSourceMode === 'upload' ? (
        <>
          {existing.length > 0 && (
            <div className="existing-media-panel">
              <div className="existing-media-head">
                <span>
                  <b>{existing.length} NFT file{existing.length === 1 ? '' : 's'} attached</b>
                  <small>{metadataCount} metadata record{metadataCount === 1 ? '' : 's'} prepared</small>
                </span>
                <div className="existing-media-tools">
                  <button
                    type="button"
                    className="secondary compact"
                    disabled={busy}
                    onClick={() => setSelected(selected.length === existing.length ? [] : existing.map(item => String(item.id)))}
                  >
                    {selected.length === existing.length ? 'Select none' : 'Select all'}
                  </button>
                  <button
                    type="button"
                    className="secondary compact danger"
                    disabled={busy || !selected.length}
                    onClick={removeSelected}
                  >
                    <X /> Remove selected
                  </button>
                  <button
                    type="button"
                    className="secondary compact danger"
                    disabled={busy}
                    onClick={clearAll}
                  >
                    Clear all
                  </button>
                </div>
              </div>
              <div className="existing-media-grid">
                {existing.map((asset, index) => {
                  const id = String(asset.id);
                  const checked = selected.includes(id);
                  return (
                    <label key={id} className={`existing-media-item${checked ? ' selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy}
                        onChange={() => toggle(id)}
                      />
                      <img
                        src={apiUrl(`/api/v1/creator/assets/${encodeURIComponent(id)}/content`)}
                        alt={asset.original_name || `NFT ${index + 1}`}
                        loading="lazy"
                      />
                      <span>
                        <b>{asset.original_name || `NFT ${index + 1}`}</b>
                        <small>Item {index + 1}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <Dropzone
            onFiles={onFiles}
            busy={busy}
            files={parsed.media}
          />
          {!!parsed.media.length && (
            <div className="file-summary">
              <div>
                <FileImage />
                <span><b>{parsed.media.length}</b><small>NFT media</small></span>
              </div>
              <div>
                <FileArchive />
                <span><b>{parsed.metadataCount}</b><small>Matching JSON</small></span>
              </div>
              <div>
                <Sparkles />
                <span>
                  <b>{parsed.media.length - parsed.metadataCount}</b>
                  <small>Auto metadata</small>
                </span>
              </div>
            </div>
          )}
          {uploadProgress && <div className="progress-note">{uploadProgress}</div>}
          <div className="draft-media-cancel-row">
            <button className="secondary danger" onClick={cancel} disabled={busy}>
              <X /> Cancel launch setup
            </button>
            <small>Available only before a launch approval or on-chain deployment.</small>
          </div>
          <div className="actions split">
            <button className="secondary" onClick={back} disabled={busy}>
              <ArrowLeft />Back
            </button>
            <button
              className="primary"
              disabled={busy || (!parsed.media.length && !existing.length)}
              onClick={upload}
            >
              {busy ? <Loader2 className="spin" /> : <ArrowRight />}
              {parsed.media.length ? 'Replace with these files' : 'Use attached files'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="external-metadata-panel">
            <div className="external-metadata-intro">
              <ExternalLink />
              <span>
                <b>Existing metadata Base URI</b>
                <small>
                  Paste the folder containing numbered JSON files, or paste a direct 0.json / 1.json URL. Rose checks both numbering styles server-side and snapshots the artwork into OnlyRose before launch.
                </small>
              </span>
            </div>
            <label className="field full">
              <span>Metadata Base URI</span>
              <input
                type="url"
                value={externalBaseUri}
                onChange={event => setExternalBaseUri(event.target.value)}
                placeholder="https://example.com/metadata/"
                disabled={externalBusy || busy}
              />
            </label>
            <div className="external-metadata-grid">
              <label className="field">
                <span>Number of NFTs</span>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={externalItemCount}
                  onChange={event => setExternalItemCount(event.target.value)}
                  placeholder={String(collection?.supply || 1)}
                  disabled={externalBusy || busy}
                />
              </label>
              <button
                type="button"
                className="secondary external-probe-button"
                onClick={() => probeExternalMetadata()}
                disabled={externalBusy || busy || !/^https?:\/\//i.test(externalBaseUri.trim())}
              >
                {externalBusy ? <Loader2 className="spin" /> : <RefreshCw />}
                Re-check metadata
              </button>
            </div>

            {externalProbe?.error && (
              <div className="external-probe-result error">
                <AlertTriangle />
                <span><b>Could not use this Base URI</b><small>{externalProbe.error}</small></span>
              </div>
            )}

            {probeReady && (
              <div className="external-probe-result success">
                <CheckCircle2 />
                <span>
                  <b>{externalProbe.first_filename} detected first</b>
                  <small>
                    {externalProbe.detected_schema || 'JSON'} · source starts at {externalProbe.first_index}.json
                    {externalProbe.preview_name ? ` · ${externalProbe.preview_name}` : ''}
                  </small>
                </span>
              </div>
            )}

            {validIndexes.length > 1 && (
              <div className="external-index-choice">
                <span>Both 0.json and 1.json are valid. Choose the actual first NFT:</span>
                <button
                  type="button"
                  className={externalProbe?.first_index === 0 ? 'active' : ''}
                  onClick={() => probeExternalMetadata(0)}
                  disabled={externalBusy || busy}
                >
                  Use 0.json first
                </button>
                <button
                  type="button"
                  className={externalProbe?.first_index === 1 ? 'active' : ''}
                  onClick={() => probeExternalMetadata(1)}
                  disabled={externalBusy || busy}
                >
                  Use 1.json first
                </button>
              </div>
            )}

            {existing.length > 0 && (
              <div className="external-existing-warning">
                <AlertTriangle />
                <span>
                  <b>{existing.length} uploaded NFT file{existing.length === 1 ? '' : 's'} are currently attached.</b>
                  <small>Choosing Base URI will replace those draft NFT files with immutable OnlyRose snapshots imported from the Base URI. Nothing already deployed on-chain is touched.</small>
                </span>
              </div>
            )}
          </div>

          <div className="actions split">
            <button className="secondary" onClick={back} disabled={busy || externalBusy}>
              <ArrowLeft />Back
            </button>
            <button
              className="primary"
              disabled={busy || externalBusy || !probeReady || !Number(externalItemCount || collection?.supply || 0)}
              onClick={useExternalMetadata}
            >
              {externalBusy ? <Loader2 className="spin" /> : <ArrowRight />}
              Use detected metadata
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function MintStep({ form, setForm, account, back, save, busy, itemCount }) {
  return (
    <section className="wizard-card">
      <div className="card-heading">
        <i>3</i>
        <div>
          <h2>Mint</h2>
          <p>Only the options needed to launch publicly.</p>
        </div>
      </div>
      <div className="form-grid">
        <label className="field">
          <span>NFT price in SOL</span>
          <input
            type="number"
            min="0"
            step="0.000001"
            value={form.priceSol}
            onChange={event => setForm({ ...form, priceSol: event.target.value })}
          />
        </label>
        <label className="field">
          <span>Per-wallet limit</span>
          <input
            type="number"
            min="1"
            max={Math.max(1, itemCount)}
            value={form.walletLimit}
            onChange={event => setForm({ ...form, walletLimit: event.target.value })}
          />
        </label>
        <label className="field full">
          <span>Creator payout wallet</span>
          <div className="input-row">
            <input
              value={form.treasury}
              onChange={event => setForm({ ...form, treasury: event.target.value })}
              placeholder="Solana wallet receiving NFT sale proceeds"
            />
            <button
              className="small"
              onClick={() => setForm({ ...form, treasury: account.address || '' })}
            >
              Use connected
            </button>
          </div>
        </label>
        <label className="field">
          <span>Royalty</span>
          <select
            value={form.royaltyBps}
            onChange={event => setForm({ ...form, royaltyBps: event.target.value })}
          >
            <option value="0">0%</option>
            <option value="250">2.5%</option>
            <option value="500">5%</option>
            <option value="750">7.5%</option>
            <option value="1000">10%</option>
          </select>
        </label>
        <label className="field">
          <span>Start</span>
          <select
            value={form.startMode}
            onChange={event => setForm({ ...form, startMode: event.target.value })}
          >
            <option value="now">Launch as soon as ready</option>
            <option value="scheduled">Schedule a date</option>
          </select>
        </label>
        {form.startMode === 'scheduled' && (
          <label className="field full">
            <span>Public mint start</span>
            <input
              type="datetime-local"
              value={form.startAt}
              onChange={event => setForm({ ...form, startAt: event.target.value })}
            />
          </label>
        )}
      </div>
      <div className="minimal-note">
        <ShieldCheck />
        <span>
          <b>Immediate reveal</b>
          <small>Advanced reveal controls stay out of the minimal launch flow.</small>
        </span>
      </div>
      <div className="actions split">
        <button className="secondary" onClick={back} disabled={busy}><ArrowLeft />Back</button>
        <button className="primary" onClick={save} disabled={busy || !form.treasury}>
          {busy ? <Loader2 className="spin" /> : <ArrowRight />}
          Review launch
        </button>
      </div>
    </section>
  );
}

function ReviewStep({
  collection,
  job,
  config,
  itemCount,
  approve,
  busy,
  back,
}) {
  const creatorPrice = Number(job?.planSummary?.mintPriceLamports || 0);
  const buyerTotal = creatorPrice + Number(job?.perMintFeeLamports || 0);
  const approvalTotal =
    Number(job?.launchFeeLamports || 0) + Number(job?.reserveLamports || 0);
  return (
    <section className="wizard-card review-card">
      <div className="card-heading">
        <i>4</i>
        <div>
          <h2>Review and launch</h2>
          <p>One wallet approval starts the entire automatic deployment.</p>
        </div>
      </div>
      <div className="review-hero">
        <div className="review-art">
          {collection?.assets?.find(item => item.role === 'item') ? (
            <img
              src={apiUrl(`/api/v1/creator/assets/${collection.assets.find(item => item.role === 'item').id}/content`)}
              alt=""
            />
          ) : <PackagePlus />}
        </div>
        <div>
          <span>COLLECTION READY</span>
          <h3>{collection?.name}</h3>
          <p>{itemCount} NFT{itemCount === 1 ? '' : 's'} · {collection?.symbol}</p>
        </div>
      </div>
      <div className="cost-list">
        <div>
          <span>Creator NFT price</span>
          <b>{sol(creatorPrice)}</b>
          <small>Paid directly to your creator payout wallet on every mint.</small>
        </div>
        <div>
          <span>Rose mint convenience fee</span>
          <b>{sol(job?.perMintFeeLamports || config?.perMintFeeLamports)}</b>
          <small>Added transparently to each buyer mint and sent to Rose treasury.</small>
        </div>
        <div className="buyer-total">
          <span>Buyer pays per mint</span>
          <b>{sol(buyerTotal)}</b>
          <small>Plus the buyer’s normal Solana network fee.</small>
        </div>
      </div>
      <div className="approval-cost">
        <div>
          <CircleDollarSign />
          <span>
            <b>One-time creator approval</b>
            <small>Rose launch service fee</small>
          </span>
          <strong>{sol(job?.launchFeeLamports)}</strong>
        </div>
        <div>
          <LockKeyhole />
          <span>
            <b>Refundable deployment reserve</b>
            <small>Funds on-chain rent and background transactions. Unused SOL returns to you.</small>
          </span>
          <strong>{sol(job?.reserveLamports)}</strong>
        </div>
        <div className="approval-total">
          <span>Total authorised now</span>
          <b>{sol(approvalTotal)}</b>
        </div>
      </div>
      {job?.collectionAddress && (
        <div className="adoption-note">
          <ShieldCheck />
          <span>
            <b>Existing Core collection will be adopted</b>
            <small>{short(job.collectionAddress)} will not be recreated or charged twice.</small>
          </span>
        </div>
      )}
      <div className="one-click">
        <Rocket />
        <div>
          <b>Approve once. Rose does the rest.</b>
          <span>Collection → public mint → metadata → Rose listing. Creator payouts stay in your treasury wallet.</span>
        </div>
      </div>
      <div className="managed-authority-note">
        <ShieldCheck />
        <span>
          <b>Managed launch authority</b>
          <small>To keep this to one approval, an encrypted Rose deployment signer remains the collection and mint authority while the public mint is live. Your NFT sale proceeds go directly to your treasury wallet.</small>
        </span>
      </div>
      <div className="actions split">
        <button className="secondary" onClick={back} disabled={busy}><ArrowLeft />Back</button>
        <button className="launch-button" onClick={approve} disabled={busy || !job}>
          {busy ? <Loader2 className="spin" /> : <Rocket />}
          Launch collection
        </button>
      </div>
    </section>
  );
}

const PROGRESS_STEPS = [
  ['queued', 'Launch authorised'],
  ['collection', 'Collection ready'],
  ['mint', 'Public mint created'],
  ['metadata', 'NFT details loaded'],
  ['authority', 'Launch authority secured'],
  ['publish', 'Mint published and listed'],
  ['live', 'Collection live'],
];

function ProgressScreen({ job, refresh, retry, topUp, repairMetadata, busy }) {
  const activeIndex = Math.max(
    0,
    PROGRESS_STEPS.findIndex(([key]) => key === job.currentStep),
  );
  return (
    <section className="wizard-card progress-card">
      <div className="success-orbit">
        {job.status === 'live' ? <CheckCircle2 /> : job.status === 'failed' ? <AlertTriangle /> : <Loader2 className="spin" />}
      </div>
      <span className="status-label">{job.status.replaceAll('_', ' ')}</span>
      <h2>{job.status === 'live' ? 'Your collection is live.' : job.status === 'failed' ? 'The automatic job stopped safely.' : 'Rose is launching your collection.'}</h2>
      <p>{job.status === 'failed' ? job.lastError : 'You can leave this page. The deployment continues in the Rose stack.'}</p>
      <div className="progress-track">
        <i style={{ width: `${job.progress}%` }} />
      </div>
      <b className="progress-number">{job.progress}%</b>
      <div className="automatic-steps">
        {PROGRESS_STEPS.map(([key, label], index) => {
          const done = job.status === 'live' || index < activeIndex;
          const active = key === job.currentStep && job.status !== 'failed';
          return (
            <div key={key} className={done ? 'done' : active ? 'active' : ''}>
              <i>{done ? <Check /> : active ? <Loader2 className="spin" /> : index + 1}</i>
              <span>{label}</span>
            </div>
          );
        })}
      </div>
      <div className="job-addresses">
        <span><small>Collection</small><b>{short(job.collectionAddress)}</b></span>
        <span><small>Public mint</small><b>{short(job.candyMachineAddress)}</b></span>
      </div>
      <div className="event-log">
        {(job.events || []).slice(-8).map(event => (
          <div key={event.id} className={event.level === 'error' ? 'error' : ''}>
            <span>{new Date(event.createdAt).toLocaleTimeString()}</span>
            <b>{event.message}</b>
          </div>
        ))}
      </div>
      {job.status === 'failed' &&
        Number(job.funding?.shortfallLamports || 0) > 0 && (
          <div className="funding-repair-card">
            <div>
              <CircleDollarSign />
              <span>
                <b>Refundable deployment shortfall</b>
                <small>
                  The launch fee is already paid. Add only the exact missing reserve and Rose resumes automatically.
                </small>
              </span>
            </div>
            <strong>
              {sol(job.funding.shortfallLamports)}
            </strong>
            <button
              className="launch-button"
              disabled={busy}
              onClick={topUp}
            >
              <Wallet />
              Fund shortfall and resume
            </button>
          </div>
        )}
      {job.status === 'live' &&
        !job.metadataRepair?.complete &&
        (
          Number(job.metadataRepair?.pendingAssets || 0) > 0 ||
          Number(job.metadataRepair?.unresolvedAssets || 0) > 0
        ) && (
          <div className="funding-repair-card">
            <div>
              <ImageIcon />
              <span>
                <b>NFT image and metadata repair</b>
                <small>
                  Rose verified the confirmed mint but its Core metadata or public image still needs repair.
                  No second launch fee is charged.
                </small>
              </span>
            </div>
            {Number(job.metadataRepair?.shortfallLamports || 0) > 0 && (
              <strong>
                {sol(job.metadataRepair.shortfallLamports)} refundable
              </strong>
            )}
            <button
              className="launch-button"
              disabled={busy}
              onClick={repairMetadata}
            >
              <ImageIcon />
              {Number(job.metadataRepair?.shortfallLamports || 0) > 0
                ? 'Fund and repair NFT metadata'
                : 'Repair NFT metadata'}
            </button>
          </div>
        )}
      <div className="actions center">
        <button className="secondary" disabled={busy} onClick={refresh}>
          <RefreshCw />Refresh
        </button>
        {job.status === 'failed' &&
          Number(job.funding?.shortfallLamports || 0) <= 0 && (
            <button className="primary" disabled={busy} onClick={retry}>
              <Rocket />Retry automatically
            </button>
          )}
        {job.status === 'live' && (
          <>
            <a className="primary link-button" href="/launchpad">
              View on Launchpad <ExternalLink />
            </a>
            <a
              className="secondary link-button"
              href={`/launchpad/${job.launchpadId}`}
            >
              Open public mint <ExternalLink />
            </a>
          </>
        )}
      </div>
    </section>
  );
}

function AdminMonitor({ open, account }) {
  const [jobs, setJobs] = useState([]);
  const [message, setMessage] = useState('Loading deployment jobs…');
  const [fees, setFees] = useState({
    launch_fee_lamports: 2000000,
    per_mint_fee_lamports: 5000,
  });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [result, config] = await Promise.all([
        orch('/admin/jobs'),
        orch('/config'),
      ]);
      setJobs(result);
      setFees({
        launch_fee_lamports: Number(config.launchFeeLamports),
        per_mint_fee_lamports: Number(config.perMintFeeLamports),
      });
      setMessage('');
    } catch (error) {
      setMessage(error.message);
    }
  };
  const saveFees = async () => {
    setBusy(true);
    try {
      const saved = await orch('/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(fees),
      });
      setFees(saved);
      setMessage('Creator launch and per-mint convenience fees saved.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);
  return (
    <main className="app-shell">
      <Header account={account} open={open} message={message} />
      <section className="monitor">
        <div className="monitor-head">
          <div><span>ADMIN ONLY</span><h2>Creator launch jobs</h2><p>The technical deployment sequence is kept here, away from creators.</p></div>
          <button className="secondary" onClick={load}><RefreshCw />Refresh</button>
        </div>
        <div className="admin-fees">
          <label className="field">
            <span>One-time launch service fee · lamports</span>
            <input
              type="number"
              min="0"
              value={fees.launch_fee_lamports}
              onChange={event => setFees({ ...fees, launch_fee_lamports: Number(event.target.value) })}
            />
            <small>{sol(fees.launch_fee_lamports)}</small>
          </label>
          <label className="field">
            <span>Rose convenience fee per mint · lamports</span>
            <input
              type="number"
              min="0"
              value={fees.per_mint_fee_lamports}
              onChange={event => setFees({ ...fees, per_mint_fee_lamports: Number(event.target.value) })}
            />
            <small>{sol(fees.per_mint_fee_lamports)}</small>
          </label>
          <button className="primary" disabled={busy} onClick={saveFees}>
            {busy ? <Loader2 className="spin" /> : <Settings />}
            Save fees
          </button>
        </div>
        {!jobs.length ? <div className="empty">No launch jobs are visible.</div> : jobs.map(job => (
          <article key={job.id}>
            <div>
              <span>{job.status}</span>
              <b>{job.planSummary?.name || job.collectionId}</b>
              <small>{job.progress}% · {job.currentStep}</small>
            </div>
            <div><small>Collection</small><b>{short(job.collectionAddress)}</b></div>
            <div><small>Mint</small><b>{short(job.candyMachineAddress)}</b></div>
            <a href={`?collection=${job.collectionId}`}><ChevronRight /></a>
          </article>
        ))}
      </section>
    </main>
  );
}

function PublicMint({ launchId, open, account, walletProvider }) {
  const [plan, setPlan] = useState(null);
  const [message, setMessage] = useState('Loading live mint…');
  const [busy, setBusy] = useState(false);
  const [record, setRecord] = useState(null);

  // Public mint pricing must come from the same on-chain Candy Guard that will
  // enforce the transaction.  Older launchpad_entries rows can contain a stale
  // or zero mint_price_lamports even though the deployed guard still charges
  // the creator price.  Keep a read-only Umi available even before a wallet is
  // connected so the public page can show the real live price.
  const readUmi = useMemo(() => (
    createUmi(browserRpcProxyUrl())
      .use(mplCore())
      .use(mplCandyMachine())
  ), []);

  const umi = useMemo(() => {
    if (!plan || !account.address || !walletProvider) return null;
    return createUmi(browserRpcProxyUrl())
      .use(mplCore())
      .use(mplCandyMachine())
      .use(walletAdapterIdentity(walletAdapter(account.address, walletProvider)));
  }, [plan, account.address, walletProvider]);

  const guardLamports = amount => {
    if (!amount) return null;
    const basisPoints = amount?.basisPoints;
    if (basisPoints === undefined || basisPoints === null) return null;
    const value = Number(basisPoints);
    return Number.isFinite(value) && value >= 0 ? value : null;
  };

  const withOnChainMintPricing = async mintPlan => {
    if (!mintPlan?.candyMachineAddress) return mintPlan;

    try {
      const candyMachine = await fetchCandyMachine(
        readUmi,
        publicKey(mintPlan.candyMachineAddress),
      );
      const candyGuard = await fetchCandyGuard(
        readUmi,
        publicKey(candyMachine.mintAuthority),
      );

      const solPaymentOption = candyGuard?.guards?.solPayment;
      const solFixedFeeOption = candyGuard?.guards?.solFixedFee;
      const solPayment = solPaymentOption?.__option
        ? unwrapOption(solPaymentOption)
        : null;
      const solFixedFee = solFixedFeeOption?.__option
        ? unwrapOption(solFixedFeeOption)
        : null;
      const onChainCreatorPrice = guardLamports(solPayment?.lamports);
      const onChainRoseFee = guardLamports(solFixedFee?.lamports);

      return {
        ...mintPlan,
        priceLamports:
          onChainCreatorPrice ?? Number(mintPlan.priceLamports || 0),
        perMintFeeLamports:
          onChainRoseFee ?? Number(mintPlan.perMintFeeLamports || 0),
        treasury:
          solPayment?.destination
            ? String(solPayment.destination)
            : mintPlan.treasury,
        roseTreasury:
          solFixedFee?.destination
            ? String(solFixedFee.destination)
            : mintPlan.roseTreasury,
        pricingSource:
          onChainCreatorPrice !== null || onChainRoseFee !== null
            ? 'candy-guard'
            : 'launch-record',
      };
    } catch (error) {
      console.warn('OnlyRose could not read live Candy Guard pricing', error);
      return mintPlan;
    }
  };

  const load = async () => {
    try {
      const mintPlan = await orch(`/public/mint-plan/${launchId}`);
      setPlan(await withOnChainMintPricing(mintPlan));
      setMessage('');
    } catch (error) {
      setMessage(error.message);
    }
  };

  const reconcileMint = async pending => {
    let latest = null;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      latest = await orch(
        `/public/mint-reconcile/${launchId}`,
        {
          method: 'POST',
          body: JSON.stringify({
            minter_wallet: pending.minterWallet,
            signature: pending.signature,
            asset_address: pending.assetAddress,
          }),
        },
      );

      if (latest.reconciled) {
        localStorage.removeItem(
          pendingMintKey(launchId),
        );
        setRecord(latest.mint);
        setMessage('Mint confirmed on-chain and recorded by OnlyRose.');
        await load();
        return latest;
      }

      setMessage(
        latest.message ||
        'Mint submitted. OnlyRose is checking the on-chain asset.',
      );
      await new Promise(resolve =>
        setTimeout(resolve, 5_000),
      );
    }

    return latest;
  };

  useEffect(() => {
    load();
  }, [launchId]);

  useEffect(() => {
    const raw = localStorage.getItem(
      pendingMintKey(launchId),
    );

    if (!raw || !account.address) return;

    let pending;

    try {
      pending = JSON.parse(raw);
    } catch {
      localStorage.removeItem(
        pendingMintKey(launchId),
      );
      return;
    }

    if (
      pending.minterWallet !==
      account.address
    ) {
      return;
    }

    setBusy(true);
    setMessage(
      'Checking the previous mint against Solana before another mint is allowed…',
    );
    reconcileMint(pending)
      .catch(error => {
        setMessage(
          `Mint reconciliation is still pending: ${error.message}`,
        );
      })
      .finally(() =>
        setBusy(false),
      );
  }, [launchId, account.address]);

  const mint = async () => {
    setBusy(true);
    try {
      await ensureVerified(account.address, walletProvider);
      if (!umi || !plan) throw new Error('Connect a Solana wallet');

      const asset = generateSigner(umi);
      const mintArgs = {
        solPayment: some({ destination: publicKey(plan.treasury) }),
        solFixedFee: some({ destination: publicKey(plan.roseTreasury) }),
      };
      if (plan.walletLimit) mintArgs.mintLimit = some({ id: 1 });

      setMessage(
        'Approve the mint once. OnlyRose will reconcile the on-chain result even if wallet confirmation times out.',
      );

      const result = await mintV1(umi, {
        candyMachine: publicKey(plan.candyMachineAddress),
        collection: publicKey(plan.collectionAddress),
        asset,
        mintArgs,
      }).send(umi);

      const pending = {
        launchId,
        minterWallet: account.address,
        signature: signatureText(result),
        assetAddress: String(asset.publicKey),
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem(
        pendingMintKey(launchId),
        JSON.stringify(pending),
      );

      setMessage(
        'Mint submitted. Checking the signature and the new Core asset on-chain…',
      );

      const reconciled = await reconcileMint(pending);

      if (!reconciled?.reconciled) {
        setMessage(
          'Mint is still being indexed. Do not click Mint again; this page will reconcile it on refresh.',
        );
      }
    } catch (error) {
      const pending = localStorage.getItem(
        pendingMintKey(launchId),
      );

      setMessage(
        pending
          ? `The wallet confirmation window ended, but OnlyRose saved the signature and will reconcile it: ${error.message}`
          : error.message,
      );
    } finally {
      setBusy(false);
    }
  };

  const total = Number(plan?.priceLamports || 0) + Number(plan?.perMintFeeLamports || 0);
  const recordAddress =
    record?.asset_address ||
    record?.assetAddress;

  return (
    <main className="app-shell public-mint">
      <Header account={account} open={open} message={message} />
      <section className="mint-panel">
        <span>ONLYROSE PUBLIC MINT</span>
        <h2>{plan?.name || 'Collection mint'}</h2>
        <p>{plan?.description}</p>
        <div className="mint-stats">
          <div><b>{plan?.mintedCount || 0}/{plan?.supply || 0}</b><small>Minted</small></div>
          <div><b>{sol(plan?.priceLamports)}</b><small>Creator price</small></div>
          <div><b>{sol(plan?.perMintFeeLamports)}</b><small>Rose fee</small></div>
          <div><b>{sol(total)}</b><small>Total</small></div>
        </div>
        <button className="launch-button" disabled={!plan || !account.address || busy} onClick={mint}>
          {busy ? <Loader2 className="spin" /> : <Rocket />}
          {record ? 'Mint another NFT' : 'Mint NFT'}
        </button>
        {recordAddress && (
          <div className="mint-record">
            <CheckCircle2 />
            <span>
              <b>Asset minted and reconciled</b>
              <small>{recordAddress}</small>
            </span>
          </div>
        )}
      </section>
    </main>
  );
}

function CreatorWizard({ open, account, walletProvider }) {
  const requestedCollectionId = new URLSearchParams(window.location.search).get('collection') || '';
  const [step, setStep] = useState(1);
  const [collections, setCollections] = useState([]);
  const [collection, setCollection] = useState(null);
  const [config, setConfig] = useState(null);
  const [job, setJob] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [parsed, setParsed] = useState({ media: [], metadataByBase: {}, metadataCount: 0 });
  const [customMetadata, setCustomMetadata] = useState([]);
  const [metadataSourceMode, setMetadataSourceMode] = useState('upload');
  const [externalBaseUri, setExternalBaseUri] = useState('');
  const [externalItemCount, setExternalItemCount] = useState('');
  const [externalProbe, setExternalProbe] = useState(null);
  const [externalBusy, setExternalBusy] = useState(false);
  const [collectionForm, setCollectionForm] = useState({
    name: '',
    symbol: '',
    description: '',
  });
  const [mintForm, setMintForm] = useState({
    priceSol: '0.05',
    walletLimit: '3',
    treasury: account.address || '',
    royaltyBps: '500',
    startMode: 'now',
    startAt: '',
  });

  useEffect(() => {
    if (account.address && !mintForm.treasury) {
      setMintForm(current => ({ ...current, treasury: account.address }));
    }
  }, [account.address]);

  const loadCollection = async id => {
    if (!id) {
      setCollection(null);
      setCollectionForm({ name: '', symbol: '', description: '' });
      setCustomMetadata([]);
      setParsed({ media: [], metadataByBase: {}, metadataCount: 0 });
      setMetadataSourceMode('upload');
      setExternalBaseUri('');
      setExternalItemCount('');
      setExternalProbe(null);
      return null;
    }
    setParsed({ media: [], metadataByBase: {}, metadataCount: 0 });
    const [detail, mediaManifest] = await Promise.all([
      api(`/creator/collections/${id}`),
      orch(`/collections/${id}/media`).catch(() => []),
    ]);
    setCollection(detail);
    const externalSource =
      detail?.metadata_mode === 'external-base' &&
      detail?.config?.metadata_source &&
      typeof detail.config.metadata_source === 'object'
        ? detail.config.metadata_source
        : null;
    if (externalSource) {
      setMetadataSourceMode('external');
      setExternalBaseUri(externalSource.base_uri || '');
      setExternalItemCount(String(externalSource.item_count || detail.supply || ''));
      setExternalProbe({ ...externalSource, valid_indexes: externalSource.valid_indexes || [externalSource.first_index] });
    } else {
      setMetadataSourceMode('upload');
      setExternalBaseUri('');
      setExternalItemCount(String(detail.supply || ''));
      setExternalProbe(null);
    }
    if (Array.isArray(mediaManifest) && mediaManifest.length) {
      setCustomMetadata(
        mediaManifest.map((item, index) => ({
          ...(item.metadata || {}),
          name:
            item.metadata?.name ||
            `${detail.name} #${index + 1}`,
          symbol:
            item.metadata?.symbol ||
            detail.symbol ||
            '',
          description:
            item.metadata?.description ||
            detail.description ||
            '',
          image:
            item.url,
          media_type:
            item.contentType,
          properties: {
            ...(item.metadata?.properties || {}),
            files: [
              {
                uri: item.url,
                type: item.contentType,
              },
            ],
          },
        })),
      );
    } else {
      setCustomMetadata([]);
    }
    setCollectionForm({
      name: detail.name || '',
      symbol: detail.symbol || '',
      description: detail.description || '',
    });
    setMintForm(current => ({
      ...current,
      priceSol: String(Number(detail.mint_price_lamports || 0) / 1e9),
      walletLimit: String(detail.wallet_limit || Math.max(1, detail.supply || 1)),
      treasury: detail.treasury_wallet || account.address || '',
      royaltyBps: String(detail.royalty_basis_points ?? 500),
      startMode: detail.start_at ? 'scheduled' : 'now',
      startAt: detail.start_at ? String(detail.start_at).slice(0, 16) : '',
    }));
    const jobs = await orch(`/jobs?collection_id=${id}`).catch(() => []);
    if (jobs[0]) {
      setJob(jobs[0]);
      if (!['awaiting_approval'].includes(jobs[0].status)) setStep(5);
      else setStep(4);
    }
    return detail;
  };

  const refreshCollections = async () => {
    const [list, feeConfig] = await Promise.all([
      api('/creator/collections').catch(() => []),
      orch('/config'),
    ]);
    setCollections(list);
    setConfig(feeConfig);
    const preferred =
      list.find(item => String(item.id) === String(requestedCollectionId)) ||
      list.find(item => item.status !== 'launched');
    if (preferred) await loadCollection(preferred.id);
  };

  useEffect(() => {
    if (!account.address) {
      orch('/config').then(setConfig).catch(error => setMessage(error.message));
      return;
    }
    ensureVerified(account.address, walletProvider)
      .then(refreshCollections)
      .catch(error => setMessage(error.message));
  }, [account.address, walletProvider]);

  useEffect(() => {
    const metadataPending =
      job?.status === 'live' &&
      !job?.metadataRepair?.complete &&
      (
        Number(job?.metadataRepair?.pendingAssets || 0) > 0 ||
        Number(job?.metadataRepair?.unresolvedAssets || 0) > 0
      );

    if (
      !job ||
      (
        !['approved', 'deploying', 'retry'].includes(job.status) &&
        !metadataPending
      )
    ) return;

    const timer = window.setInterval(async () => {
      try {
        const updated = await orch(`/jobs/${job.id}`);
        setJob(updated);
      } catch {}
    }, 2500);
    return () => window.clearInterval(timer);
  }, [job?.id, job?.status]);

  const saveCollection = async () => {
    setBusy('collection');
    try {
      await ensureVerified(account.address, walletProvider);
      if (!collectionForm.name.trim()) throw new Error('Enter the collection name');
      const payload = {
        name: collectionForm.name.trim(),
        symbol: collectionForm.symbol.trim(),
        description: collectionForm.description.trim(),
        category: 'Digital Art',
        content_rating: 'standard',
        asset_standard: 'core',
        visibility: 'public',
      };
      const result = collection
        ? await api(`/creator/collections/${collection.id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : await api('/creator/collections', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
      setCollection(result);
      setCollections(current =>
        collection
          ? current.map(item => item.id === result.id ? result : item)
          : [result, ...current],
      );
      setStep(2);
      setMessage('Collection saved. Add the NFT files.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const parseSelectedFiles = async inputFiles => {
    setMetadataSourceMode('upload');
    setExternalProbe(null);
    setBusy('parse');
    setMessage('');
    try {
      let files = [];
      for (const file of inputFiles) {
        if (/\.zip$/i.test(file.name)) {
          const zip = await JSZip.loadAsync(file);
          for (const [name, entry] of Object.entries(zip.files)) {
            if (entry.dir || name.includes('__MACOSX')) continue;
            const blob = await entry.async('blob');
            files.push(new File([blob], name.split('/').pop(), { type: blob.type }));
          }
        } else {
          files.push(file);
        }
      }
      const metadataByBase = {};
      const media = [];
      for (const file of files) {
        const base = file.name.replace(/\.[^.]+$/, '').toLowerCase();
        if (/\.json$/i.test(file.name)) {
          try {
            metadataByBase[base] = JSON.parse(await file.text());
          } catch {
            throw new Error(`${file.name} is not valid JSON`);
          }
        } else if (ACCEPTED_MEDIA.test(file.name) || file.type.startsWith('image/') || file.type.startsWith('video/')) {
          media.push({ file, base });
        }
      }
      media.sort((a, b) => a.file.name.localeCompare(b.file.name, undefined, { numeric: true }));
      setParsed({
        media,
        metadataByBase,
        metadataCount: media.filter(item => metadataByBase[item.base]).length,
      });
      setMessage(`${media.length} NFT media file${media.length === 1 ? '' : 's'} prepared.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const probeExternalMetadata = async (
    preferredFirstIndex = null,
    quiet = false,
  ) => {
    if (!collection || !externalBaseUri.trim()) return null;
    setExternalBusy(true);
    try {
      const result = await api(
        `/creator/collections/${collection.id}/metadata/external-base/probe`,
        {
          method: 'POST',
          body: JSON.stringify({
            base_uri: externalBaseUri.trim(),
            ...(
              preferredFirstIndex === 0 || preferredFirstIndex === 1
                ? { preferred_first_index: preferredFirstIndex }
                : {}
            ),
          }),
        },
      );
      setExternalProbe(result);
      if (result.base_uri && result.base_uri !== externalBaseUri) {
        setExternalBaseUri(result.base_uri);
      }
      if (!externalItemCount && Number(collection.supply || 0) > 0) {
        setExternalItemCount(String(collection.supply));
      }
      if (!quiet) {
        setMessage(
          `Detected ${result.first_filename} as the first NFT metadata file. ${result.detected_schema || 'JSON'} mode selected.`,
        );
      }
      return result;
    } catch (error) {
      setExternalProbe({ error: error.message });
      if (!quiet) setMessage(error.message);
      return null;
    } finally {
      setExternalBusy(false);
    }
  };

  useEffect(() => {
    if (
      metadataSourceMode !== 'external' ||
      !collection?.id ||
      !/^https?:\/\//i.test(externalBaseUri.trim())
    ) return undefined;

    const timer = window.setTimeout(() => {
      probeExternalMetadata(null, true);
    }, 750);

    return () => window.clearTimeout(timer);
  }, [metadataSourceMode, externalBaseUri, collection?.id]);

  const useExternalMetadata = async () => {
    if (!collection) return;
    const count = Number(externalItemCount || collection.supply || 0);
    if (!Number.isInteger(count) || count < 1 || count > 10000) {
      setMessage('Enter the number of NFTs in the existing metadata set (1 to 10,000).');
      return;
    }
    if (!(externalProbe?.first_index === 0 || externalProbe?.first_index === 1)) {
      setMessage('Re-check the Base URI so Rose can detect whether 0.json or 1.json is first.');
      return;
    }

    const existingCount = (collection.assets || []).filter(item => item.role === 'item').length;
    if (
      existingCount > 0 &&
      !window.confirm(
        `Use Base URI and replace ${existingCount} attached draft NFT file${existingCount === 1 ? '' : 's'}? ` +
        'OnlyRose will import and snapshot the Base URI artwork. Nothing already deployed on-chain is touched.',
      )
    ) return;

    setExternalBusy(true);
    try {
      if (existingCount > 0) {
        const cleared = await clearDraftMedia();
        if (cleared === false) throw new Error('Could not clear the previous draft NFT files.');
      }

      setMessage(`Importing and snapshotting ${count} NFT metadata record${count === 1 ? '' : 's'}…`);
      const saved = await api(
        `/creator/collections/${collection.id}/metadata/external-base`,
        {
          method: 'POST',
          body: JSON.stringify({
            base_uri: externalBaseUri.trim(),
            item_count: count,
            preferred_first_index: Number(externalProbe.first_index),
          }),
        },
      );
      setCollection(saved.collection);
      setMetadataSourceMode('external');
      setExternalProbe(saved.metadata_source);
      setExternalBaseUri(saved.metadata_source.base_uri);
      setExternalItemCount(String(saved.metadata_source.item_count));
      setCustomMetadata([]);
      setParsed({ media: [], metadataByBase: {}, metadataCount: 0 });
      await loadCollection(collection.id);
      setStep(3);
      setMessage(
        `Base URI imported: ${saved.metadata_source.item_count} NFT${saved.metadata_source.item_count === 1 ? '' : 's'} snapshotted into OnlyRose, starting from ${saved.metadata_source.first_filename}.`,
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setExternalBusy(false);
    }
  };

  const uploadMedia = async () => {
    if (!collection) return;

    setMetadataSourceMode('upload');
    setBusy('upload');
    try {
      const nextConfig = { ...(collection.config || {}) };
      delete nextConfig.metadata_source;
      await api(`/creator/collections/${collection.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ metadata_mode: 'auto', config: nextConfig }),
      });
      const existingAssets =
        (collection.assets || [])
          .filter(item => item.role === 'item')
          .sort(
            (left, right) =>
              Number(left.position || 0) -
              Number(right.position || 0),
          );
      const existingMetadata =
        (collection.metadata_items || [])
          .slice()
          .sort(
            (left, right) =>
              Number(left.item_index || 0) -
              Number(right.item_index || 0),
          );

      let mediaItems =
        parsed.media.map(item => ({
          ...item,
          assetId: null,
          existingMetadata: null,
        }));

      if (
        !mediaItems.length &&
        existingAssets.length &&
        customMetadata.length === existingAssets.length &&
        customMetadata.every(item => /^https?:\/\//i.test(String(item?.image || '')))
      ) {
        setStep(3);
        setMessage(
          `${existingAssets.length} persisted NFT file${existingAssets.length === 1 ? '' : 's'} are ready.`,
        );
        return;
      }

      if (!mediaItems.length && existingAssets.length) {
        setUploadProgress(
          'Publishing the artwork already attached to this collection…',
        );

        mediaItems = [];

        for (
          let index = 0;
          index < existingAssets.length;
          index += 1
        ) {
          const asset =
            existingAssets[index];
          const response = await fetch(
            apiUrl(`/api/v1/creator/assets/${asset.id}/content`),
            {
              credentials: 'include',
            },
          );

          if (!response.ok) {
            throw new Error(
              `Could not read attached artwork ${asset.original_name || index + 1}`,
            );
          }

          const blob =
            await response.blob();
          const fileName =
            asset.original_name ||
            `nft-${index + 1}`;
          const file = new File(
            [blob],
            fileName,
            {
              type:
                asset.content_type ||
                blob.type ||
                'application/octet-stream',
            },
          );

          mediaItems.push({
            file,
            base:
              fileName
                .replace(/\.[^.]+$/, '')
                .toLowerCase(),
            assetId: asset.id,
            existingMetadata:
              existingMetadata[index] || null,
          });
        }
      }

      if (!mediaItems.length) {
        throw new Error(
          'Add at least one NFT media file before continuing',
        );
      }

      const newAssetIds = [];
      const newPublicMediaIds = [];
      const metadata = [];

      for (
        let index = 0;
        index < mediaItems.length;
        index += 1
      ) {
        const item =
          mediaItems[index];

        let assetId =
          item.assetId;

        if (!assetId) {
          setUploadProgress(
            `Uploading ${index + 1} of ${mediaItems.length}: ${item.file.name}`,
          );
          const body =
            new FormData();
          body.append(
            'file',
            item.file,
          );
          const uploaded =
            await jsonRequest(
              apiUrl('/api/v1/creator/assets'),
              {
                method: 'POST',
                body,
              },
            );
          assetId = uploaded.id;
          newAssetIds.push(
            assetId,
          );
        }

        setUploadProgress(
          `Publishing ${index + 1} of ${mediaItems.length}: ${item.file.name}`,
        );

        const storedMetadata =
          item.existingMetadata &&
          typeof item.existingMetadata ===
            'object'
            ? {
                ...(
                  item.existingMetadata.metadata &&
                  typeof item.existingMetadata.metadata ===
                    'object'
                    ? item.existingMetadata.metadata
                    : {}
                ),
                name:
                  item.existingMetadata.name,
                description:
                  item.existingMetadata.description,
                attributes:
                  item.existingMetadata.attributes,
              }
            : {};

        const custom = {
          ...storedMetadata,
          ...(
            parsed.metadataByBase[
              item.base
            ] || {}
          ),
        };

        const publicMedia =
          await uploadPublicMedia(
            collection.id,
            item.file,
            custom,
          );

        if (!item.assetId && publicMedia?.id) {
          newPublicMediaIds.push(String(publicMedia.id));
        }

        metadata.push({
          ...custom,
          name:
            custom.name ||
            `${collection.name} #${index + 1}`,
          symbol:
            custom.symbol ||
            collection.symbol ||
            '',
          description:
            custom.description ||
            collection.description ||
            '',
          image:
            publicMedia.url,
          media_type:
            publicMedia.contentType,
          properties: {
            ...(
              custom.properties &&
              typeof custom.properties ===
                'object'
                ? custom.properties
                : {}
            ),
            category:
              publicMedia.contentType
                .startsWith('video/')
                ? 'video'
                : 'image',
            files: [
              {
                uri:
                  publicMedia.url,
                type:
                  publicMedia.contentType,
              },
            ],
          },
        });
      }

      setUploadProgress(
        'Attaching files and preparing public metadata…',
      );

      if (newAssetIds.length) {
        if (newPublicMediaIds.length !== newAssetIds.length) {
          throw new Error('Rose could not bind every uploaded NFT to its public media record. Nothing was attached to the draft.');
        }

        await orch(
          `/collections/${collection.id}/draft-media/replace`,
          {
            method: 'POST',
            body: JSON.stringify({
              asset_ids: newAssetIds,
              public_media_ids: newPublicMediaIds,
            }),
          },
        );

        await api(
          `/creator/collections/${collection.id}/assets`,
          {
            method: 'POST',
            body: JSON.stringify({
              asset_ids:
                newAssetIds,
              role: 'item',
            }),
          },
        );
      }

      await api(
        `/creator/collections/${collection.id}/metadata/generate`,
        {
          method: 'POST',
          body: JSON.stringify({
            name_pattern:
              '{collection} #{number}',
            description:
              collection.description ||
              '',
            external_url: '',
          }),
        },
      );

      const detail = await api(
        `/creator/collections/${collection.id}`,
      );
      const itemCount =
        detail.assets?.filter(
          item =>
            item.role === 'item',
        ).length || 0;
      const updated = await api(
        `/creator/collections/${collection.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            supply: itemCount,
            asset_standard: 'core',
          }),
        },
      );

      setCollection(updated);
      setCustomMetadata(metadata);
      await loadCollection(collection.id);
      setStep(3);
      setMessage(
        `${metadata.length} NFT media and metadata record${metadata.length === 1 ? '' : 's'} are publicly ready.`,
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
      setUploadProgress('');
    }
  };

  const retainDraftMedia = async retainAssetIds => {
    if (!collection) return;
    setBusy('media-control');
    setMessage('Updating the unlaunched NFT file set…');
    try {
      const result = await orch(
        `/collections/${collection.id}/draft-media/retain`,
        {
          method: 'POST',
          body: JSON.stringify({ retain_asset_ids: retainAssetIds }),
        },
      );
      await loadCollection(collection.id);
      setParsed({ media: [], metadataByBase: {}, metadataCount: 0 });
      setMessage(`${result.count} NFT file${result.count === 1 ? '' : 's'} remain attached to this draft.`);
      return true;
    } catch (error) {
      setMessage(error.message);
      return false;
    } finally {
      setBusy('');
    }
  };

  const clearDraftMedia = async () => retainDraftMedia([]);

  const cancelDraftLaunch = async () => {
    if (!collection) return;
    setBusy('media-control');
    setMessage('Cancelling the unlaunched setup and clearing its NFT files…');
    try {
      await orch(
        `/collections/${collection.id}/draft-media/cancel`,
        {
          method: 'POST',
          body: '{}',
        },
      );
      await loadCollection(collection.id);
      setParsed({ media: [], metadataByBase: {}, metadataCount: 0 });
      setJob(null);
      setStep(1);
      setMessage('Launch setup cancelled. No on-chain collection or Candy Machine was touched.');
      return true;
    } catch (error) {
      setMessage(error.message);
      return false;
    } finally {
      setBusy('');
    }
  };

  const saveMint = async () => {
    setBusy('mint');
    try {
      if (!collection) throw new Error('Collection is missing');
      new PublicKey(mintForm.treasury);
      const externalCount =
        collection?.metadata_mode === 'external-base'
          ? Number(collection?.config?.metadata_source?.item_count || collection.supply || 0)
          : 0;
      const itemCount =
        externalCount ||
        collection.assets?.filter(item => item.role === 'item').length ||
        collection.supply ||
        0;
      const priceLamports = Math.round(Number(mintForm.priceSol || 0) * 1e9);
      const walletLimit = Math.max(
        1,
        Math.min(Number(mintForm.walletLimit || 1), Math.max(1, itemCount)),
      );
      const updated = await api(`/creator/collections/${collection.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          mint_price_lamports: priceLamports,
          wallet_limit: walletLimit,
          treasury_wallet: mintForm.treasury,
          royalty_basis_points: Number(mintForm.royaltyBps),
          reveal_mode: 'immediate',
          start_at:
            mintForm.startMode === 'scheduled' && mintForm.startAt
              ? new Date(mintForm.startAt).toISOString()
              : null,
          end_at: null,
          supply: itemCount,
          asset_standard: 'core',
        }),
      });
      await api(`/creator/collections/${collection.id}/readiness`, { method: 'POST' });
      setCollection(updated);
      const createdJob = await orch('/jobs', {
        method: 'POST',
        body: JSON.stringify({
          collection_id: collection.id,
          creator_wallet: account.address,
          custom_metadata: customMetadata,
        }),
      });
      setJob(createdJob);
      setStep(4);
      setMessage('Review the exact fee and approve once.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const approve = async () => {
    if (!job || !config) return;
    setBusy('approve');
    try {
      await ensureVerified(account.address, walletProvider);
      setMessage('Confirm the single OnlyRose launch approval in your wallet.');
      const signature = await sendOneLaunchApproval({
        account,
        walletProvider,
        job,
        browserRpcUrl: browserRpcProxyUrl(),
      });
      setMessage('Approval sent. Rose is verifying and starting the automatic launch.');
      const updated = await orch(`/jobs/${job.id}/approval`, {
        method: 'POST',
        body: JSON.stringify({ signature }),
      });
      setJob(updated);
      setStep(5);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const refreshJob = async () => {
    if (!job) return;
    setBusy('refresh');
    try {
      setJob(await orch(`/jobs/${job.id}`));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const retryJob = async () => {
    if (!job) return;
    setBusy('retry');
    try {
      setJob(await orch(`/jobs/${job.id}/retry`, { method: 'POST' }));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const topUpJob = async () => {
    if (!job || !config) return;
    setBusy('topup');
    try {
      await ensureVerified(
        account.address,
        walletProvider,
      );
      const shortfall = Number(
        job.funding?.shortfallLamports || 0,
      );
      if (shortfall <= 0) {
        throw new Error(
          'No deployment funding shortfall remains',
        );
      }
      setMessage(
        `Approve ${sol(shortfall)} refundable deployment funding. No second launch fee is charged.`,
      );
      const signature =
        await sendFundingTopUp({
          account,
          walletProvider,
          job,
          browserRpcUrl:
            browserRpcProxyUrl(),
        });
      setMessage(
        'Funding sent. Rose is verifying it and resuming the automatic launch.',
      );
      const updated = await orch(
        `/jobs/${job.id}/topup`,
        {
          method: 'POST',
          body: JSON.stringify({
            signature,
          }),
        },
      );
      setJob(updated);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };


  const repairMetadataJob = async () => {
    if (!job || !config) return;
    setBusy('metadata-repair');

    try {
      await ensureVerified(
        account.address,
        walletProvider,
      );
      const shortfall = Number(
        job.metadataRepair?.shortfallLamports || 0,
      );
      let signature = null;

      if (shortfall > 0) {
        setMessage(
          `Approve ${sol(shortfall)} refundable metadata-repair funding. No second launch fee is charged.`,
        );
        signature = await sendFundingTopUp({
          account,
          walletProvider,
          job,
          browserRpcUrl:
            browserRpcProxyUrl(),
          shortfallLamports: shortfall,
          memoPrefix:
            'onlyrose-metadata-repair',
        });
      }

      setMessage(
        'Rose is verifying the on-chain asset, metadata JSON and image.',
      );
      const updated = await orch(
        `/jobs/${job.id}/metadata-repair`,
        {
          method: 'POST',
          body: JSON.stringify({
            signature,
          }),
        },
      );
      setJob(updated);
      setMessage(
        'NFT metadata and public media are verified. Wallet indexers may take a short time to refresh their cached card.',
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy('');
    }
  };

  const externalItemTotal =
    collection?.metadata_mode === 'external-base'
      ? Number(collection?.config?.metadata_source?.item_count || collection?.supply || 0)
      : 0;
  const itemCount =
    externalItemTotal ||
    collection?.assets?.filter(item => item.role === 'item').length ||
    collection?.supply ||
    parsed.media.length ||
    0;

  if (!account.address) {
    return (
      <main className="app-shell">
        <Header account={account} open={open} message={message} />
        <section className="connect-first">
          <div className="connect-icon"><Wallet /></div>
          <span>ONE SIMPLE CREATOR FLOW</span>
          <h2>Connect the Solana wallet that owns the collection.</h2>
          <p>Create the collection, drop the NFT files, review the cost and approve once.</p>
          <button className="launch-button" onClick={() => open({ view: 'Connect' })}>
            <Wallet />Connect Solana wallet
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <Header account={account} open={open} message={message} />
      {step <= 4 && <Stepper step={step} />}
      <div className="wizard-wrap">
        {step === 1 && (
          <CollectionStep
            collections={collections}
            collection={collection}
            form={collectionForm}
            setForm={setCollectionForm}
            selectCollection={id => loadCollection(id).catch(error => setMessage(error.message))}
            save={saveCollection}
            busy={busy === 'collection'}
          />
        )}
        {step === 2 && (
          <MediaStep
            collection={collection}
            parsed={parsed}
            onFiles={parseSelectedFiles}
            upload={uploadMedia}
            retainMedia={retainDraftMedia}
            clearMedia={clearDraftMedia}
            cancelLaunch={cancelDraftLaunch}
            busy={['parse', 'upload', 'media-control'].includes(busy)}
            uploadProgress={uploadProgress}
            back={() => setStep(1)}
            metadataSourceMode={metadataSourceMode}
            setMetadataSourceMode={setMetadataSourceMode}
            externalBaseUri={externalBaseUri}
            setExternalBaseUri={setExternalBaseUri}
            externalItemCount={externalItemCount}
            setExternalItemCount={setExternalItemCount}
            externalProbe={externalProbe}
            externalBusy={externalBusy}
            probeExternalMetadata={probeExternalMetadata}
            useExternalMetadata={useExternalMetadata}
          />
        )}
        {step === 3 && (
          <MintStep
            form={mintForm}
            setForm={setMintForm}
            account={account}
            back={() => setStep(2)}
            save={saveMint}
            busy={busy === 'mint'}
            itemCount={itemCount}
          />
        )}
        {step === 4 && (
          <ReviewStep
            collection={collection}
            job={job}
            config={config}
            itemCount={itemCount}
            approve={approve}
            busy={busy === 'approve'}
            back={() => setStep(3)}
          />
        )}
        {step === 5 && job && (
          <ProgressScreen
            job={job}
            refresh={refreshJob}
            retry={retryJob}
            topUp={topUpJob}
            repairMetadata={repairMetadataJob}
            busy={Boolean(busy)}
          />
        )}
      </div>
      <footer className="footer">
        <a href="/">Return to OnlyRose</a>
        <span>{RELEASE}</span>
        <a href="?mode=admin">Admin deployment monitor</a>
      </footer>
    </main>
  );
}


export function LaunchCollectionPage() {
  const { open } = useAppKit();
  const account = useAppKitAccount({ namespace: 'solana' });
  const { walletProvider } = useAppKitProvider('solana');
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const launchId = params.get('launch');
  const content = mode === 'admin'
    ? <AdminMonitor open={open} account={account} />
    : mode === 'mint' && launchId
      ? <PublicMintResolver launchId={launchId} open={open} account={account} walletProvider={walletProvider} />
      : <CreatorWizard open={open} account={account} walletProvider={walletProvider} />;
  return <div className="rose-unified-launch">{content}</div>;
}

export function PublicLaunchMintPage({ launchId }) {
  const { open } = useAppKit();
  const account = useAppKitAccount({ namespace: 'solana' });
  const { walletProvider } = useAppKitProvider('solana');
  return (
    <div className="rose-unified-launch">
      <PublicMintResolver launchId={launchId} open={open} account={account} walletProvider={walletProvider} />
    </div>
  );
}

export function LaunchAdminMonitorPage() {
  const { open } = useAppKit();
  const account = useAppKitAccount({ namespace: 'solana' });
  return (
    <div className="rose-unified-launch">
      <AdminMonitor open={open} account={account} />
    </div>
  );
}
